import { useState, useEffect, useCallback } from "react";
import { Megaphone, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";

const LAST_SEEN_KEY = "sorvaine_announcements_last_seen";

const dotStyles = (isUnread: boolean) =>
  isUnread
    ? "bg-primary"
    : "bg-transparent border border-muted-foreground/30";

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: string;
  created_at: string;
  target_type: string;
  target_org_id: string | null;
}

export function AnnouncementBell() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState<string>(() => {
    return localStorage.getItem(LAST_SEEN_KEY) || new Date(0).toISOString();
  });

  const fetchAnnouncements = useCallback(async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from("announcements")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);

    if (!error && data) {
      setAnnouncements(data as Announcement[]);
    }
  }, [user?.id]);

  // Initial fetch + polling every 60s
  useEffect(() => {
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 60_000);
    return () => clearInterval(interval);
  }, [fetchAnnouncements]);

  const unreadCount = announcements.filter(
    (a) => a.created_at > lastSeen
  ).length;

  const markAllRead = () => {
    // Use the latest announcement's server timestamp to avoid client/server clock skew
    const latestAnnouncement = announcements[0]?.created_at;
    const now = new Date().toISOString();
    const markTime = latestAnnouncement && latestAnnouncement > now ? latestAnnouncement : now;
    localStorage.setItem(LAST_SEEN_KEY, markTime);
    setLastSeen(markTime);
  };

  const recordViews = useCallback(async () => {
    if (!user?.id || announcements.length === 0) return;
    try {
      const rows = announcements.map((a) => ({
        announcement_id: a.id,
        user_id: user.id,
      }));
      await supabase.from("announcement_views").upsert(rows, {
        onConflict: "announcement_id,user_id",
        ignoreDuplicates: true,
      });
    } catch (e) {
      console.error("Failed to record announcement views", e);
    }
  }, [user?.id, announcements]);

  const handleOpen = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      if (unreadCount > 0) {
        markAllRead();
      }
      recordViews();
    }
  };

  if (!user) return null;

  const popoverContent = (
    <div className="w-[380px] bg-popover border border-border rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.08)]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Announcements</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      <ScrollArea className="max-h-[400px]">
        {announcements.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No announcements yet
          </div>
        ) : (
          <div className="py-1">
            {announcements.map((a) => {
              const isUnread = a.created_at > lastSeen;
              return (
                <div
                  key={a.id}
                  className={cn(
                    "px-4 py-3 border-b border-border/30 last:border-b-0 transition-colors",
                    isUnread && "border-l-2 border-l-primary bg-primary/[0.04]"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center shrink-0 mt-2 w-7">
                      <div className={cn("w-2.5 h-2.5 rounded-full", dotStyles(isUnread))} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-foreground truncate">{a.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{a.message}</p>
                      <span className="text-[10px] text-muted-foreground/60 mt-1 block">
                        {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center transition-colors relative",
            isOpen
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          )}
        >
          <Megaphone className="w-[18px] h-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C2541A] text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {isOpen && createPortal(
        <PopoverContent
          side="right"
          align="end"
          sideOffset={12}
          className="p-0 border-0 bg-transparent shadow-none w-auto z-[1000]"
        >
          {popoverContent}
        </PopoverContent>,
        document.body
      )}
    </Popover>
  );
}

// Keep backward compat export
export function AnnouncementBanner() {
  return null; // Replaced by AnnouncementBell in sidebar
}
